import { useEffect } from 'react';
import isEmpty from 'lodash/isEmpty';
import { useCloseOnSuccessQueryStringValue } from './useQueryString';
import { isProductionHost } from '../../../common/utils';

type ActionFunction = (redirectUrl: string) => void;

const DEFAULT_REDIRECT_PLAN_HOST = 'https://plan.foreflight.com';
const LOCALHOST_REDIRECT_URL = 'http://localhost:8100';

/**
 * Custom hook to handle redirection or closing the window on successful login.
 *
 * In some edge cases, the login flow can be opened in a separate window for better
 * context awareness. If this window was opened by another window (e.g., plan.foreflight.com),
 * it sends a success message back to the opener and attempts to close itself.
 *
 * For local development, the message may include a redirect URL with a signed token
 * to facilitate login from QA environments. This functionality is not available in Production.
 *
 * @param {string} redirectUrl - The URL to redirect to on success.
 * @param {ActionFunction} action - Optional action function to execute with the redirect URL.
 */
const useRedirectOrCloseOnSuccess = (redirectUrl: string, action?: ActionFunction | undefined) => {
    const isCloseOnSuccess = useCloseOnSuccessQueryStringValue();

    const redirectToUrl = (tryWindowClose: boolean) => {
        if (tryWindowClose) {
            try {
                window.close();
            } catch (error) {
                console.log('Cannot close window - not opened by script');
            }
        }
        window.location.href = redirectUrl;
    };

    useEffect(() => {
        if (!isEmpty(redirectUrl)) {
            // Handle popup window scenario
            if (window.opener) {
                let targetOrigin;
                try {
                    const url = new URL(redirectUrl);
                    targetOrigin = `${url.protocol}//${url.host}`;
                } catch (error) {
                    console.log('Invalid redirect URL:', redirectUrl);
                    targetOrigin = DEFAULT_REDIRECT_PLAN_HOST;
                }
                // If the redirectUrl has a localhost redirect and not in Production, we return the redirectUrl to the opener.
                // This will allow the opener to handle the redirect for localhost logins. Otherwise, the user is logged
                // in by the session cookie and the opener can just reload for login success.
                const message =
                    redirectUrl.startsWith(LOCALHOST_REDIRECT_URL) && !isProductionHost()
                        ? redirectUrl
                        : 'login-success';
                window.opener.postMessage(message, targetOrigin);
            }
            redirectToUrl(isCloseOnSuccess);
        }

        if (action) {
            action(redirectUrl);
        }
    }, [redirectUrl, isCloseOnSuccess]);
};
export default useRedirectOrCloseOnSuccess;
